import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { AceCheckbox } from '@/accessControl/AceCheckbox.molecule';
import { AcePopover } from '@/accessControl/AcePopover.molecule';
import { AclModalMainTableRow } from '@/accessControl/AclModalMainTableRow.molecule';
import { CorporateDriveMessage } from '@/accessControl/CorporateDriveMessage.molecule';
import { GetLinks } from '@/accessControl/GetLinks.molecule';
import { prettyIdentityType } from '@/accessControl/itemAclModal.utilities';
import { EVERYONE_USERGROUP } from '@/administration/administration.constants';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { SelectIdentity } from '@/core/SelectIdentity.molecule';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { useHomeScreenCurrentTab } from '@/homescreen/homescreen.store';
import { HOME_SCREEN_TABS, PERMISSIONS } from '@/main/app.constants';
import { isExternalOrganizationTabEnabled } from '@/services/systemConfiguration.utilities';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { SeeqNames } from '@seeqdev/constants';
import { faInfoCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Alert, Button, Icon } from '@seeqdev/qomponents';
import type { Ace, Acl, Identity } from '@/accessControl/itemAclModal.utilities';
import type { IdentityOption } from '@/core/SelectIdentity.molecule';
import type { ACLPermissionName } from '@/main/app.constants';

// Mirrors Organization.ORGANIZATION_SHAREABLE_TYPES in the appserver and must be kept in sync.
const ORGANIZATION_SHAREABLE_TYPES = new Set<string>([
  SeeqNames.Types.Topic,
  SeeqNames.Types.Analysis,
  SeeqNames.Types.Asset,
]);

type AclModalMainTabProps = {
  item?: any;
  fetchInherited?: any;
  loading: boolean;
  onClose?: any;
  saving: boolean;
  aclModified?: any;
  displayAceEntries: Ace[];
  currentUserId: string;
  setPermissions: (permission: ACLPermissionName, ace: Ace) => void;
  newAce: Ace;
  isAclFromDatasource: boolean;
  addItemAce: () => void;
  itemAcl: Acl;
  setIdentity: (Identity: Identity) => void;
  userHasManagePermission: boolean;
  workbookId: string;
  worksheetId?: string;
  includeLinksAndCorporateMessage: boolean;
  folderId?: string;
  showOnlyFolderLinks?: boolean;
  itemId: string;
  itemName: string;
  isAclModalLocationInHeader: boolean;
  isItemAncestorNotCorporate: boolean;
  isVantage: boolean;
  hasMultipleOrganizations?: boolean;
  itemType: string;
};

export const AclModalMainTab = ({
  item,
  loading,
  onClose,
  fetchInherited,
  saving,
  aclModified,
  displayAceEntries,
  currentUserId,
  setPermissions,
  newAce,
  isAclFromDatasource,
  addItemAce,
  setIdentity,
  itemAcl,
  userHasManagePermission,
  workbookId,
  worksheetId,
  includeLinksAndCorporateMessage,
  folderId,
  showOnlyFolderLinks,
  itemId,
  itemName,
  isAclModalLocationInHeader,
  isItemAncestorNotCorporate,
  isVantage,
  hasMultipleOrganizations,
  itemType,
}: AclModalMainTabProps) => {
  const { t } = useTranslation();
  const homeScreenCurrentTab = useHomeScreenCurrentTab();
  const { isTopic } = useFlux(sqWorkbookStore);

  const validIdentitySelected = _.has(newAce, 'identity.id');

  // When external orgs feature is enabled:
  //   - Topic/Analysis/Asset: exclude external org users, but include org groups
  //   - Other entities (Folder, etc.): exclude both external org users and org groups
  // When disabled: don't pass params (undefined) → API defaults to true (show all)
  const externalOrgEnabled = isExternalOrganizationTabEnabled();
  const isOrganizationShareableType = ORGANIZATION_SHAREABLE_TYPES.has(itemType);
  const includeExternalOrgUsers = externalOrgEnabled ? false : undefined;
  const includeExternalOrgGroups = externalOrgEnabled ? isOrganizationShareableType : undefined;

  const tableHeader = (
    <div className="flexColumnContainer fakeTableHeader">
      <div className="tw:min-w-sq-450!">{t('ACCESS_CONTROL.HEADER.NAME')}</div>
      <div className="tw:min-w-sq-70!">{t('ACCESS_CONTROL.HEADER.TYPE')}</div>
      {_.map(
        [
          'ACCESS_CONTROL.ACCESS_LEVELS.READ',
          'ACCESS_CONTROL.ACCESS_LEVELS.WRITE',
          'ACCESS_CONTROL.ACCESS_LEVELS.MANAGE',
        ],
        (level) => (
          <div className="text-center tw:w-sq-75!" key={level}>
            {t(level)}
          </div>
        ),
      )}
    </div>
  );

  const popover = (ace: Ace) => (
    <div id={`${ace.id}_popover`} className="tw:ml-sq-5">
      <AcePopover ace={ace} />
    </div>
  );

  const tableBody = (
    <div className="flexRowContainer fakeTableWrapper">
      {_.map(displayAceEntries, (ace, index) => {
        return (
          <div key={ace.identity?.id ?? ace.id ?? index} className="fakeTableStriped">
            <AclModalMainTableRow
              ace={ace}
              index={index}
              popover={popover}
              currentUserId={currentUserId}
              setPermissions={setPermissions}
              isAclFromDatasource={isAclFromDatasource}
              displayAceEntries={displayAceEntries}
              isItemAncestorNotCorporate={isItemAncestorNotCorporate}
              includeLinksAndCorporateMessage={includeLinksAndCorporateMessage}
              userHasManagePermission={userHasManagePermission}
            />

            {includeLinksAndCorporateMessage &&
              ace.identity?.name === EVERYONE_USERGROUP &&
              ace.permissions?.read &&
              ace.identity.isEnabled &&
              isItemAncestorNotCorporate &&
              homeScreenCurrentTab !== HOME_SCREEN_TABS.CORPORATE && (
                <CorporateDriveMessage
                  item={item}
                  itemId={itemId}
                  itemName={itemName}
                  isAclModalLocationInHeader={isAclModalLocationInHeader}
                  aclModified={aclModified}
                  fetchInherited={fetchInherited}
                  onClose={onClose}
                />
              )}
          </div>
        );
      })}
    </div>
  );

  const newAceRow = userHasManagePermission && (
    <div id="specNewEntryRow" className="flexColumnContainer tw:mt-sq-5 tw:items-center" data-testid="newEntryRow">
      <div className="tw:mr-12.5 tw:w-sq-400! tw:min-w-sq-400!">
        <SelectIdentity
          idForLabel="identityName"
          setIdentity={setIdentity}
          allowGroups={true}
          identity={newAce.identity as IdentityOption}
          includeAllProperties={true}
          showDirectory={true}
          startEditable={true}
          placeholder="ACCESS_CONTROL.USER_PLACEHOLDER"
          clearIdentityWhenEmpty={true}
          maxHeight="450px"
          positionFixed={true}
          includeExternalOrgUsers={includeExternalOrgUsers}
          includeExternalOrgGroups={includeExternalOrgGroups}
        />
      </div>

      <div className="text-valign-middle tw:min-w-sq-70!">
        {newAce.identity?.type ? prettyIdentityType(newAce.identity.type, t) : ''}
      </div>
      {_.map(PERMISSIONS, (perm) => (
        <div key={`${perm}_new`} className="text-center text-valign-middle footerCheckbox tw:min-w-sq-75!">
          <AceCheckbox name={perm} ace={newAce} setPermissions={setPermissions} isAclFromDatasource={false} />
        </div>
      ))}
      <div className="text-valign-middle tw:min-w-sq-50! tw:text-right">
        <Button
          id="specAddPermissionButton"
          type="button"
          extraClassNames="mr-3"
          size="sm"
          variant="theme"
          disabled={!validIdentitySelected}
          onClick={addItemAce}
          label={t('ADD')}
        />
      </div>
    </div>
  );

  const assetSelectionWarning = isTopic && (
    <Alert variant="gray" dismissible={false} extraClassNames="mt10 mb10">
      <span>
        <Trans
          i18nKey="ITEM_ACL.WARNING_MESSAGE"
          components={{
            Emphasis: <strong />,
          }}
        />
        <Icon
          icon={faInfoCircle}
          extraClassNames="m3"
          type="info"
          tooltipPlacement="right"
          tooltip={t('ITEM_ACL.WARNING_MESSAGE_TOOLTIP')}
          onClick={() => {
            window.open(`${SUPPORT_LINK}/authorization`, '_blank');
          }}
        />
      </span>
    </Alert>
  );

  const multipleOrganizationsError = hasMultipleOrganizations && (
    <Alert
      variant="danger"
      dismissible={false}
      extraClassNames="mt10 mb10 tw:text-white"
      testId="multiple-organizations-error">
      {t('ACCESS_CONTROL.MULTIPLE_ORGANIZATIONS_ERROR', {
        itemType,
      })}
    </Alert>
  );

  return (
    <>
      {loading && (
        <div
          className="flexRowContainer tw:mt-7.5 tw:mb-sq-15 tw:items-center tw:justify-center"
          data-testid="loadingACLMain">
          <span className="tw:mb-sq-5">{t('ITEM_ACL.LOADING')}</span>
          <IconWithSpinner spinning={true} />
        </div>
      )}
      {saving && (
        <div className="flexRowContainer tw:mb-sq-15 tw:items-center tw:justify-center">
          <span className="tw:mb-sq-5">{t('ITEM_ACL.SAVING')}</span>
          <IconWithSpinner spinning={true} />
        </div>
      )}

      {!saving && !loading && (
        <div id="aclFormGroup" className="flexRowContainer flexFill overflowVisible tw:mt-sq-15">
          {t('ITEM_ACL.LABEL')}

          {assetSelectionWarning}
          {multipleOrganizationsError}

          {itemAcl.permissionsInheritanceDisabled && (
            <span className="sq-text-warning cursorPointer link-no-underline">
              {t('ITEM_ACL.ADVANCED.DISABLED_INDICATOR')}
            </span>
          )}

          {isAclFromDatasource && <div className="sq-text-warning tw:italic">{t('ITEM_ACL.FROM_DATASOURCE')}</div>}

          <div className="flexRowContainer tw:mt-sq-10">
            {tableHeader}
            {tableBody}
            {!isAclFromDatasource && newAceRow}
          </div>

          <div className="flexRowContainer tw:mt-7.5">
            {((includeLinksAndCorporateMessage && worksheetId) || showOnlyFolderLinks) && (
              <GetLinks
                workbookId={workbookId}
                worksheetId={worksheetId}
                folderId={folderId}
                showOnlyFolderLinks={showOnlyFolderLinks}
                hasViewOnly={!isVantage}
                hasPresentation={!isVantage}
                isVantage={isVantage}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
